import { useMemo, useState } from 'react';
import { FieldListObject, Filter } from '@fabric-ui/components';
import { FilterTypes, generateOption } from '@fabric-ui/utils';
import { cleanEmptyFilters, filterToExpression } from '@utils/filterUtils';
import { prettyFieldName } from '../utils/utils';

export const extractMeasurementDescription = (option: unknown, measurements: Record<string, { description?: string }>) => {
  const value = option && typeof option === 'object' && 'value' in option && typeof option.value === 'string' ? option.value : undefined;
  return value ? measurements?.[value]?.description : undefined;
};

export const useSelectivityFilter = (defaultFilter: Record<string, string[]>, fieldTypeMap: Record<string, any> = {}) => {
  const [filters, setFilters] = useState<Record<string, string[]>>(defaultFilter);
  const [selectivityFilter, setSelectivityFilter] = useState<Filter | undefined>(
    cleanEmptyFilters(filterToExpression(fieldTypeMap, defaultFilter))
  );
  const onApplyFilter = (currentFilters = filters) =>
    setSelectivityFilter(cleanEmptyFilters(filterToExpression(fieldTypeMap, currentFilters)));
  const hasNewFilters = useMemo(
    () => JSON.stringify(selectivityFilter) !== JSON.stringify(cleanEmptyFilters(filterToExpression(fieldTypeMap, filters))),
    [filters, selectivityFilter, fieldTypeMap]
  );
  return {
    filters,
    setFilters,
    onApplyFilter,
    hasNewFilters,
    selectivityFilter,
    setSelectivityFilter,
  };
};

const flatArrayOfOptions = (data: Array<{ value: string | string[] } | null | undefined> = []): Array<{ title: string; value: string }> =>
  [
    ...new Set(
      data
        .filter((item): item is { value: string | string[] } => item != null)
        .map(({ value }) => value)
        .flat(1) as string[]
    ),
  ].map(generateOption);

export const getOptionsByField = {
  [FilterTypes.Technique]: flatArrayOfOptions,
  [FilterTypes.Asset]: flatArrayOfOptions,
  [FilterTypes.Tags]: flatArrayOfOptions,
  [FilterTypes.AssetType]: flatArrayOfOptions,
  [FilterTypes.TechniqueV2]: flatArrayOfOptions,
  [FilterTypes.AssetV2]: flatArrayOfOptions,
  [FilterTypes.TagsV2]: flatArrayOfOptions,
  [FilterTypes.AssetTypeV2]: flatArrayOfOptions,
};

// TODO: remove when cleaning newSchemaEntitiesMode
const labels: Record<string, string> = {
  'ticket.all_vulnerabilities_count': 'Findings',
  'ticket.all_asset_count': 'Assets',
  'ticket.integration_info.key': 'External Ticket ID',
};

export const blackListFields = [
  'ticket.locking_key',
  'ticket.dedup_key',
  'ticket.is_fixable',
  'ticket.detection_sources',
  'ticket.finding_count',
  'ticket.asset_count',
  'ticket.vulnerabilities_count',
  'ticket.original_severity_score',
  'ticket.all_finding_count',
  'ticket.last_seen',
  'ticket.comments',
  'ticket.details',
  'ticket.activity_logs',
  'ticket.details_str',
  'ticket.severity_optimization_reasons',
  'ticket.integration_info',
  'ticket.type',
  'ticket.remediation_time',
  'ticket.previous_dedup_key',
  'finding.source',
  'finding.asset_mac',
  'finding.asset_hostname',
  'finding.type',
  'finding.asset_username',
  'finding.details',
  'finding.details_str',
  'finding.details_hash',
  'finding.previous_dedup_key',
  'finding.dedup_key_real',
  'finding.json_details',
  'finding.all_fix_versions',
  'finding.owner',
];

export const getOptionsFromFields = (
  fields: FieldListObject[] = [],
  group: string | null = null,
  entity: string | null = null
): { title: string; value: string; group: string | null }[] =>
  fields
    .filter(({ name, builtIn }) => !builtIn && !blackListFields.includes(`${entity}.${name}`))
    .map(({ name }) => ({
      title: labels[`${entity}.${name}`] || prettyFieldName(name),
      value: entity ? `${entity}.${name}` : name,
      group,
    }));
